// LEGACY: Contains significant business logic in component. Consider extracting logic into custom hooks, utility functions, or /shared/ directory.
/* eslint-disable filename-export/match-default-export */
/* disabling rule until refactor of analytics builder */

import { isNil } from 'lodash';
import { DEFAULT_WINDOW_VARIABLE_NAME } from './analytics-builder-advanced-settings';
import { HOSTING_TYPES } from './analytics-builder-file-hosting';
import prepareAJSSnippet from './prepareAJSSnippet';
import prepareStandAloneSyncSnippet from './prepareStandAloneSyncSnippet';
import buildMRHostedFileURL from './buildMRHostedFileUrl';
import buildSettingsTemplate from './buildSettingsTemplate';
import hasCustomCdnPath from './hasCustomCdnPath';

const preparePathToUpdate = (path, part, isFirst) => {
  return isFirst ? `window.${part}` : `${path}.${part}`;
};

const prepareValueToUpdate = (path, part, isFirst, isLast) => {
  if (isLast) {
    return '[]';
  }
  return isFirst ? `window.${part} || {}` : `${path}.${part} || {}`;
};

const prepareWindowVarName = windowVariableNameParts => {
  return windowVariableNameParts.reduce(
    (acc, part, index, arr) => {
      const isFirst = index === 0;
      const isLast = index === arr.length - 1;
      const path = preparePathToUpdate(acc.path, part, isFirst);
      const value = prepareValueToUpdate(acc.path, part, isFirst, isLast);
      return {
        path,
        updatedWindowVar: `${acc.updatedWindowVar}${path} = ${value}\n`,
      };
    },
    { path: '', updatedWindowVar: '' }
  ).updatedWindowVar;
};

const isSinglePartName = splitName => splitName.length === 1;

export const initializeWindowVar = windowVariableName => {
  const windowVariableNameParts = windowVariableName.split('.');
  if (isSinglePartName(windowVariableNameParts)) {
    return '';
  }
  return prepareWindowVarName(windowVariableNameParts);
};

const prepareSnippet = (
  writekey,
  cdnURL,
  isCompressed,
  windowVariableName = DEFAULT_WINDOW_VARIABLE_NAME,
  fileHostingType,
  isStandAloneSync,
  syncInjectorSettings
) => {
  if (isStandAloneSync) {
    const src = buildStandAloneSyncSrc({ writekey, cdnURL, fileHostingType });
    return prepareStandAloneSyncSnippet(syncInjectorSettings, src);
  }
  return prepareAJSSnippet(writekey, cdnURL, isCompressed, windowVariableName, fileHostingType);
};

// Same rule as the AJS loader: the cluster serves uploaded files from the pipeline path,
// but a cdnURL with a custom path is a CS-managed location and is loaded as given.
function buildStandAloneSyncSrc({ writekey, cdnURL, fileHostingType }) {
  if (isNil(cdnURL) || fileHostingType !== HOSTING_TYPES.metaRouter) {
    return cdnURL;
  }
  return hasCustomCdnPath(cdnURL) ? cdnURL : buildMRHostedFileURL(cdnURL, writekey);
}

/**
 *
 * Create analyticsSnippet and settingsTemplate for analytics builder
 *
 * @param {{ stagedIdentitySyncsData, stagedIntegrationsData, formState, data, writekey  }}
 *
 * @returns {object}
 */

const createAjsBuilderFile = ({
  stagedIdentitySyncsData,
  stagedIntegrationsData,
  data,
  writekey,
  crossDomainSettings,
  optimizeBuild,
  consentCategories,
  queueTasks,
  hasConsentModule,
  isStandAloneSync,
}) => {
  const settingsTemplate = buildSettingsTemplate({
    stagedIdentitySyncsData,
    stagedIntegrationsData,
    data,
    writekey,
    crossDomainSettings,
    optimizeBuild,
    consentCategories,
    queueTasks,
    hasConsentModule,
  });

  const { cdnURL, fileCompression, windowVariableName, fileHostingType } = data;
  const isCompressed = fileCompression === 'true';

  const analyticsSnippet = prepareSnippet(
    writekey,
    cdnURL,
    isCompressed,
    windowVariableName,
    fileHostingType,
    isStandAloneSync,
    settingsTemplate.syncInjectorSettings
  );

  return {
    analyticsSnippet: analyticsSnippet,
    settingsTemplate: settingsTemplate,
  };
};

export const getLoadFunctionByHostingType = (hostingType, cdnURL, extension, hasPathName) => {
  const legacyAndSelfHostingSnippet = `function(key, e) {
        var t = document.createElement('script')
        t.type = 'text/javascript'
        t.async = !0
        t.src = ("https:"===document.location.protocol?"https://":"http://")+"${cdnURL}/"+key+"${extension}"
        var n = document.getElementsByTagName('script')[0]
        n.parentNode.insertBefore(t, n)
        analytics._loadOptions = e
      }`;

  if (hasPathName) {
    return legacyAndSelfHostingSnippet;
  }

  switch (hostingType) {
    case HOSTING_TYPES.self:
      return legacyAndSelfHostingSnippet;
    case HOSTING_TYPES.metaRouter:
      return `function(w, e) {
        var t = document.createElement('script')
        t.type = 'text/javascript'
        t.async = !0
        t.src = ("https:"===document.location.protocol?"https://":"http://")+"${cdnURL}/v1/pipelines/"+w+"/file"
        var n = document.getElementsByTagName('script')[0]
        n.parentNode.insertBefore(t, n)
        analytics._loadOptions = e
      }`;
    default:
      // throw error
      throw new Error('No valid hosting type provided');
  }
};

export default createAjsBuilderFile;
